/**
 * 제휴업체 배정 서비스
 * - 관리자: 서비스 요청에 파트너 배정
 * - 파트너: 배정 상태 변경 (unread → read → consulting → reserved → completed/cancelled)
 */
import { db } from '../firebase/config';
import { collection, addDoc, updateDoc, getDoc, getDocs, doc, query, where, orderBy, serverTimestamp } from 'firebase/firestore';
import { createCommission, createConsultingCommission } from './commissionService';
import { getSiteSettings } from './settingsService';

// 이사 타입 키 정규화 (여러 variant → admin 스키마에 맞게)
const normalizeMovingType = (key) => {
  if (!key) return 'pack';
  const k = String(key).toLowerCase();
  if (['pack', 'full_pack', 'package'].includes(k)) return 'pack';
  if (['semi_pack', 'half_pack', 'semi', 'semi_package'].includes(k)) return 'semi_pack';
  if (['normal', 'cargo', 'van', 'simple', 'ordinary', 'general', 'regular'].includes(k)) return 'normal';
  return k;
};

// 수수료 테이블 기반 완료가(미수금 기준) 계산
// 열람가(unit) 가 아닌 완료가(complete) 필드를 사용 — DbPricing UI 의 "완료가" 행과 일치
// 정책: 이사/청소는 평당 (complete × 평수, max 캡), 인테리어/인터넷TV는 고정 (complete 단일)
export const calcCompletionPrice = async (category, { movingType, pyeong = 25, internetType } = {}) => {
  const s = await getSiteSettings();
  const p = Number(pyeong) > 0 ? Number(pyeong) : 25;

  if (category === 'moving') {
    const type = normalizeMovingType(movingType);
    const tier = (s.movingTiers && s.movingTiers[type]) || {};
    const completeRate = Number(tier.complete) || 0;
    if (completeRate <= 0) return 0;
    const cap = Number(tier.max) || 0;
    const raw = completeRate * p;
    return cap > 0 ? Math.min(raw, cap) : raw;
  }
  if (category === 'cleaning') {
    const c = s.cleaningPrice || {};
    const completeRate = Number(c.complete) || 0;
    if (completeRate <= 0) return 0;
    const cap = Number(c.max) || 0;
    const raw = completeRate * p;
    return cap > 0 ? Math.min(raw, cap) : raw;
  }
  if (category === 'internet_tv') {
    // 고정 — internet_type 별 분리 (internet_only / internet_tv)
    const isOnly = internetType === 'internet_only';
    const priceObj = isOnly ? s.internetPriceOnly : s.internetPrice;
    return Number(priceObj?.complete) || Number(s.internetPrice?.complete) || 0;
  }
  if (category === 'interior') {
    // 정책 변경: 인테리어 완료가도 고정 (평당 X)
    return Number(s.interiorPrice?.complete) || 0;
  }
  // aircon / water_purifier / kiosk → industryDefaults.complete fallback
  const ind = (s.industryDefaults || []).find((row) => row.key === category);
  return Number(ind?.complete) || 0;
};

// 예약완료 시 미수금(partner_receivables) 자동 생성
const createReceivableForReserved = async (assignmentId, assignmentData) => {
  try {
    const partnerId = assignmentData.partner_id;
    if (!partnerId) return;

    // 중복 생성 방지
    const existing = await getDocs(
      query(collection(db, 'partner_receivables'), where('assignment_id', '==', assignmentId))
    );
    if (!existing.empty) return;

    // SR 에서 카테고리/평수/이사타입/인터넷타입 조회
    let category = '';
    let movingType = '';
    let internetType = '';
    let pyeong = 25;
    const srId = assignmentData.service_request_id;
    if (srId) {
      const srDoc = await getDoc(doc(db, 'service_requests', srId));
      if (srDoc.exists()) {
        const sr = srDoc.data();
        category = sr.category || (Array.isArray(sr.services) ? sr.services[0] : '') || '';
        movingType = sr.moving_type || '';
        internetType = sr.internet_type || '';
        pyeong = Number(sr.area_pyeong_exact) || pyeong;
      }
    }

    // 수수료 테이블 기반 완료가 계산
    const completionPrice = await calcCompletionPrice(category, { movingType, pyeong, internetType });
    const supportAmount = Number(assignmentData.support_amount) || 0;
    const receivableAmount = Math.max(0, completionPrice - supportAmount);
    if (receivableAmount <= 0) return;

    // 결제기한: 생성일 + 7일 23:59 (KST). 이음 정책 (2026-05-27 결정).
    const dueObj = new Date();
    dueObj.setDate(dueObj.getDate() + 7);
    const kstFmt = new Intl.DateTimeFormat('en-CA', {
      timeZone: 'Asia/Seoul', year: 'numeric', month: '2-digit', day: '2-digit',
    });
    const dueIso = `${kstFmt.format(dueObj)}T23:59:00+09:00`;

    await addDoc(collection(db, 'partner_receivables'), {
      partner_id: partnerId,
      assignment_id: assignmentId,
      service_request_id: srId || null,
      amount: receivableAmount,
      original_price: completionPrice,
      support_amount: supportAmount,
      category,
      moving_type: movingType || null,
      pyeong,
      label: '계약완료 수수료',
      status: 'pending',
      due_date: dueIso,
      created_at: serverTimestamp(),
    });
  } catch (err) {
    console.error('미수금 자동 생성 실패:', err);
  }
};

// ───── 배정 ─────

export const assignPartner = async (serviceRequestId, partnerId, assignedBy) => {
  const now = serverTimestamp();

  // service_requests 업데이트
  await updateDoc(doc(db, 'service_requests', serviceRequestId), {
    hq_status: 'assigned',
    assigned_partner_id: partnerId,
    assigned_at: now,
    assigned_by: assignedBy || null,
    updated_at: now,
  });

  // partner_assignments 생성
  const ref = await addDoc(collection(db, 'partner_assignments'), {
    service_request_id: serviceRequestId,
    partner_id: partnerId,
    status: 'unread',
    created_at: now,
    updated_at: now,
  });

  return ref.id;
};

// ───── 배정 목록 조회 ─────

export const getAssignmentsByPartner = async (partnerId) => {
  const q = query(
    collection(db, 'partner_assignments'),
    where('partner_id', '==', partnerId),
    orderBy('created_at', 'desc')
  );
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
};

// ───── 배정 상세 (service_request + customer 조인) ─────

export const getAssignmentDetail = async (assignmentId) => {
  const aDoc = await getDoc(doc(db, 'partner_assignments', assignmentId));
  if (!aDoc.exists()) return null;
  const assignment = { id: aDoc.id, ...aDoc.data() };

  // service_request 조인
  try {
    const srDoc = await getDoc(doc(db, 'service_requests', assignment.service_request_id));
    if (srDoc.exists()) {
      assignment.serviceRequest = { id: srDoc.id, ...srDoc.data() };

      // customer 조인
      const customerId = srDoc.data().customer_id;
      if (customerId) {
        const cDoc = await getDoc(doc(db, 'customers', customerId));
        if (cDoc.exists()) {
          assignment.customer = { id: cDoc.id, ...cDoc.data() };
        }
      }
    }
  } catch { /* skip */ }

  return assignment;
};

// ───── 배정 상태 변경 ─────

export const updateAssignmentStatus = async (assignmentId, newStatus, data = {}) => {
  const now = serverTimestamp();

  await updateDoc(doc(db, 'partner_assignments', assignmentId), {
    status: newStatus,
    ...data,
    updated_at: now,
  });

  // consulting/visiting(상담 진행) 시 부동산 view "02.상담" 반영 + 상담수수료 생성
  if (newStatus === 'consulting' || newStatus === 'visiting') {
    try {
      const aDoc = await getDoc(doc(db, 'partner_assignments', assignmentId));
      if (aDoc.exists()) {
        const srId = aDoc.data().service_request_id;
        if (srId) {
          // hq_status를 'read'로 업데이트 (부동산 view '02.상담' 카운트에 반영)
          await updateDoc(doc(db, 'service_requests', srId), {
            hq_status: 'read',
            updated_at: now,
          });

          const srDoc = await getDoc(doc(db, 'service_requests', srId));
          if (srDoc.exists() && newStatus === 'consulting') {
            await createConsultingCommission({ id: srDoc.id, ...srDoc.data() });
          }
        }
      }
    } catch (err) {
      console.error('상담 전환 후처리 실패:', err);
    }
  }

  // reserved(계약완료) 시 미수금 + 부동산 수수료 모두 생성
  if (newStatus === 'reserved') {
    try {
      const aDoc = await getDoc(doc(db, 'partner_assignments', assignmentId));
      if (aDoc.exists()) {
        const assignmentData = aDoc.data();
        await createReceivableForReserved(assignmentId, assignmentData);

        const srId = assignmentData.service_request_id;
        if (srId) {
          const srDoc = await getDoc(doc(db, 'service_requests', srId));
          if (srDoc.exists()) {
            // 중복 생성 방지 — 같은 SR의 conversion 타입이 이미 있으면 skip
            // (consulting 타입이 있어도 conversion은 별개 생성)
            const existing = await getDocs(query(
              collection(db, 'commissions'),
              where('service_request_id', '==', srId),
              where('type', '==', 'conversion'),
            ));
            if (existing.empty) {
              await createCommission({ id: srDoc.id, ...srDoc.data() }, assignmentData);
            }
            await updateDoc(doc(db, 'service_requests', srId), {
              hq_status: 'settlement_check',
              updated_at: now,
            });
          }
        }
      }
    } catch (err) {
      console.error('계약완료 후처리 실패:', err);
    }
  }
};
